import { formatInTimeZone } from "date-fns-tz";

/**
 * Canonical all-day bounds normalizer. Converts an all-day event's stored
 * bounds to BROWSER-LOCAL day bounds so it renders on — and buckets to — the
 * correct local calendar day(s) regardless of the mismatch between the source
 * timezone and the browser timezone.
 *
 * Why this is needed: Google all-day events arrive as date strings parsed as
 * UTC midnight (`new Date("2026-04-30")` → 2026-04-30T00:00Z) with an EXCLUSIVE
 * next-day end; availability marks carry an explicit `tzid`. Feeding those raw
 * instants into a browser-local day grid (react-big-calendar cells) or
 * local-day bucketing (lib/days.js) mis-places them: in `America/New_York`,
 * `new Date("2026-05-06")` is May 5 8:00 PM local, so a one-day all-day event
 * would span TWO local days. A bare `end - 1ms` trim only papers over the case
 * where the browser tz equals the source tz.
 *
 * Strategy: recover the first/last calendar day in the SOURCE tz (`tzid`,
 * default UTC — the zone that recovers Google's date-string day), then anchor
 * browser-local Dates at midnight of the first day and end-of-day (23:59:59.999)
 * of the last day.
 *
 * This is the single definition used by the availability calendar
 * (`utils/calendar-events.js`), the scheduler overlay lanes
 * (`scheduler/utils/calendar-overlay-layout.js#normalizeAllDayOverlayBounds`),
 * and the create-surface busy events (`scheduler/utils/busy-overlay-events.js`).
 *
 * @param {{startUtc: string, endUtc: string, tzid?: string|null}} input
 * @returns {{start: Date, end: Date}|null} null when the input is missing,
 *   unparseable, or inverted (caller should skip the event).
 */
/**
 * The yyyy-MM-dd of the INCLUSIVE last covered day for an all-day event whose
 * stored `endUtc` is the EXCLUSIVE next-day-midnight boundary. The last covered
 * day is the one containing (end − 1ms), read in the source `tzid` (default
 * UTC — the zone that recovers Google's date-string day). The single definition
 * of the "−1ms then format the day" trick used by `normalizeAllDayBounds` and
 * the mark editor's inclusive end-date display.
 *
 * @param {string|number|Date} exclusiveEndUtc
 * @param {string} [tzid]
 * @returns {string|null} yyyy-MM-dd, or null for missing/unparseable input.
 */
export function inclusiveLastDayYmd(exclusiveEndUtc, tzid) {
  const end = exclusiveEndUtc ? new Date(exclusiveEndUtc) : null;
  if (!end || isNaN(end.getTime())) return null;
  return formatInTimeZone(new Date(end.getTime() - 1), tzid || "UTC", "yyyy-MM-dd");
}

export function normalizeAllDayBounds({ startUtc, endUtc, tzid } = {}) {
  const start = startUtc ? new Date(startUtc) : null;
  const end = endUtc ? new Date(endUtc) : null;
  if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
    return null;
  }
  const sourceTz = tzid || "UTC";
  const firstYmd = formatInTimeZone(start, sourceTz, "yyyy-MM-dd");
  // The last covered day is one millisecond before the exclusive end.
  const lastYmd = inclusiveLastDayYmd(end, sourceTz);
  const [fy, fm, fd] = firstYmd.split("-").map(Number);
  const [ly, lm, ld] = lastYmd.split("-").map(Number);
  const startLocal = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
  const endLocal = new Date(ly, lm - 1, ld, 23, 59, 59, 999);
  if (endLocal.getTime() < startLocal.getTime()) return null;
  return { start: startLocal, end: endLocal };
}
